import { CatalogImportApi } from '@backstage/plugin-catalog-import';

// Backstage's own convention for spec.lifecycle -- see
// https://backstage.io/docs/features/software-catalog/descriptor-format#speclifecycle-required
const BACKSTAGE_LIFECYCLES = ['experimental', 'production', 'deprecated'];
const DEFAULT_LIFECYCLE = 'experimental';

// Zubale's own preset values for spec.type (no fixed enum in core Backstage --
// this is just what we've standardized on internally).
const ZUBALE_TYPES = ['internal', 'api', 'web', 'portal'];
const DEFAULT_TYPE = 'internal';

// RepoLocationAnalyzer (the generic analyzer used when a repo has no
// catalog-info.yaml yet -- see @backstage/plugin-catalog-backend) always
// emits `type: other` / `lifecycle: unknown` placeholders, and the wizard
// never exposes them as editable form fields. This wraps the real
// CatalogImportApi to swap those placeholders for Zubale's defaults (in the
// entities returned from analyzeUrl, so the wizard's preview step already
// shows the real values, not "other"/"unknown"), add an allowed-values
// comment above each in the committed file, and give the resulting PR a
// conventional-commit title/body by default instead of the plugin's
// generic "Add catalog-info.yaml config file".
export class ZubaleCatalogImportClient implements CatalogImportApi {
  constructor(private readonly inner: CatalogImportApi) {}

  async analyzeUrl(url: string) {
    const result = await this.inner.analyzeUrl(url);
    if (result.type !== 'repository') {
      return result;
    }
    return {
      ...result,
      generatedEntities: result.generatedEntities.map(entity => ({
        ...entity,
        spec: {
          ...entity.spec,
          type: DEFAULT_TYPE,
          lifecycle: DEFAULT_LIFECYCLE,
        },
      })),
    };
  }

  async preparePullRequest() {
    return {
      title: 'chore(catalog): register component in Backstage',
      body: `This pull request adds a **Backstage entity metadata file** to this repository so the component can be added to the Zubale Backstage software catalog.

Before merging, double check the \`type\` and \`lifecycle\` fields in the generated file actually match this repo -- they default to \`${DEFAULT_TYPE}\` / \`${DEFAULT_LIFECYCLE}\`.

After this pull request is merged, the component will become available in the catalog.`,
    };
  }

  submitPullRequest(options: {
    repositoryUrl: string;
    fileContent: string;
    title: string;
    body: string;
  }) {
    return this.inner.submitPullRequest({
      ...options,
      fileContent: withZubaleComments(options.fileContent),
    });
  }
}

function withZubaleComments(fileContent: string): string {
  return fileContent
    .replace(
      /^(\s*)type: .*$/m,
      `$1# Zubale type presets: ${ZUBALE_TYPES.join(', ')}\n$&`,
    )
    .replace(
      /^(\s*)lifecycle: .*$/m,
      `$1# Backstage lifecycle convention: ${BACKSTAGE_LIFECYCLES.join(', ')}\n$&`,
    );
}
